import { Charge } from "@/pages/sy/models/Charge";
import { useUpsertChargeMutation } from "@/pages/sy/hooks/queries/useChargeMutations";

type UseRecipeHandlersProps = {
  currentOperatorId: number | null;
  currentChargeNumber: number | null;
  setCurrentChargeNumber: (chargeNumber: number | null) => void;
  charge: Charge | null;
  setShowRecipeConfirmModal: (showRecipeConfirmModal: boolean) => void;
  setSelectedRecipeId: (selectedRecipeId: number | null) => void;
  setIsRecipeDialogOpen: (isRecipeDialogOpen: boolean) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
  refreshStaticData?: (options?: { recipes?: boolean }) => Promise<void>;
};

export const useRecipeHandlers = ({
  currentOperatorId,
  currentChargeNumber,
  setCurrentChargeNumber,
  charge,
  setShowRecipeConfirmModal,
  setSelectedRecipeId,
  setIsRecipeDialogOpen,
  setError,
  setNotificationMessage,
  refreshStaticData,
}: UseRecipeHandlersProps) => {
  const upsertChargeMutation = useUpsertChargeMutation();

  const handleRecipeSelect = (recipeId: number) => {
    if (!charge?.ChargeId) return;

    const chargeToUpdate: Charge = {
      ...charge,
      RecipeId: recipeId,
      OperatorId: currentOperatorId,
      ChargeNumber: currentChargeNumber || charge.ChargeNumber || 1,
    };

    upsertChargeMutation.mutate(chargeToUpdate, {
      onSuccess: () => {
        setNotificationMessage("Recipe updated successfully");
      },
      onError: (error) => {
        setError(
          "Error updating recipe: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleOpenRecipeDialog = async () => {
    if (!charge) return;
    if (charge.ChargeStatusCode === "H" || (charge.ChargeNumber ?? 0) > 3)
      return;

    if (refreshStaticData) {
      await refreshStaticData({ recipes: true });
    }

    setSelectedRecipeId(charge?.RecipeId || null);
    setCurrentChargeNumber(charge?.ChargeNumber || null);
    setIsRecipeDialogOpen(true);
  };

  const handleCloseRecipeDialog = () => {
    setIsRecipeDialogOpen(false);
    setSelectedRecipeId(charge?.RecipeId || null);
    setCurrentChargeNumber(charge?.ChargeNumber || null);
  };

  const handleConfirmRecipeModal = (selectedRecipeId: number | null) => {
    if (selectedRecipeId) {
      handleRecipeSelect(selectedRecipeId);
    } else {
      setSelectedRecipeId(charge?.RecipeId || null);
      setCurrentChargeNumber(charge?.ChargeNumber || null);
    }
    setShowRecipeConfirmModal(false);
  };

  return {
    handleRecipeSelect,
    handleOpenRecipeDialog,
    handleCloseRecipeDialog,
    handleConfirmRecipeModal,
    isRecipeMutating: upsertChargeMutation.isPending,
  };
};
